<template>
  <div class="option-select" :class="{ open }" @click.stop @keydown.esc="$emit('close')">
    <button
      type="button"
      class="option-select-control"
      :disabled="disabled"
      :aria-label="ariaLabel"
      :aria-expanded="open"
      aria-haspopup="listbox"
      @click="$emit('toggle')"
    >
      <span v-if="badge" class="option-select-badge" aria-hidden="true">{{ badge }}</span>
      <span class="option-select-value">
        <slot name="value" :option="selectedOption">
          {{ selectedOption?.label || '' }}
        </slot>
      </span>
      <span class="option-select-arrow" aria-hidden="true"></span>
    </button>

    <div v-if="open" class="option-select-menu" role="listbox">
      <button
        v-for="option in options"
        :key="option.value"
        type="button"
        class="option-select-item"
        :class="{ selected: option.value === modelValue }"
        role="option"
        :aria-selected="option.value === modelValue"
        @click="$emit('select', option.value)"
      >
        <span class="option-select-item-label">
          <slot name="option" :option="option">
            {{ option.label }}
          </slot>
        </span>
        <span class="option-select-check" aria-hidden="true"></span>
      </button>
    </div>
  </div>
</template>

<script>
export default {
  name: 'OptionSelect',
  props: {
    modelValue: {
      type: [String, Number],
      default: '',
    },
    options: {
      type: Array,
      default: () => [],
    },
    open: {
      type: Boolean,
      default: false,
    },
    disabled: {
      type: Boolean,
      default: false,
    },
    badge: {
      type: String,
      default: '',
    },
    ariaLabel: {
      type: String,
      default: '',
    },
  },
  emits: ['toggle', 'select', 'close'],
  computed: {
    selectedOption() {
      return this.options.find(option => option.value === this.modelValue) || null
    },
  },
}
</script>

<style scoped>
.option-select {
  position: relative;
  width: 100%;
  min-width: 0;
  z-index: 1;
}

.option-select.open {
  z-index: 30;
}

.option-select-control {
  display: grid;
  grid-template-columns: auto minmax(0, 1fr) auto;
  align-items: center;
  gap: var(--space-2);
  width: 100%;
  min-height: 44px;
  padding: 0 var(--space-3);
  color: var(--color-text);
  background: var(--color-surface);
  border: 1px solid var(--color-border-strong);
  border-radius: var(--radius-md);
  font: inherit;
  font-size: var(--font-size-sm);
  font-weight: 600;
  text-align: left;
  cursor: pointer;
  transition: border-color 0.18s ease, box-shadow 0.18s ease, background 0.18s ease;
}

.option-select-control:hover,
.open .option-select-control {
  border-color: color-mix(in srgb, var(--signal-active-strong) 45%, var(--color-border));
  background: color-mix(in srgb, var(--color-surface) 94%, var(--color-brand-50));
}

.option-select-control:focus-visible {
  border-color: var(--signal-active-strong);
  box-shadow: var(--focus-ring-brand);
  outline: none;
}

.option-select-control:disabled {
  color: var(--color-text-subtle);
  background: var(--color-surface-muted);
  cursor: not-allowed;
}

.option-select-badge {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 24px;
  height: 24px;
  padding: 0 0.3rem;
  color: var(--signal-active-strong);
  background: var(--color-brand-50);
  border: 1px solid color-mix(in srgb, var(--signal-active) 32%, var(--color-border));
  border-radius: var(--radius-sm);
  font-family: var(--font-family-data);
  font-size: var(--font-size-xs);
  font-weight: 600;
}

.option-select-value,
.option-select-item-label {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.option-select-arrow {
  width: 8px;
  height: 8px;
  margin: -4px 2px 0 0;
  color: var(--color-text-muted);
  border-right: 2px solid currentColor;
  border-bottom: 2px solid currentColor;
  transform: rotate(45deg);
  transition: transform 0.18s ease;
}

.open .option-select-arrow {
  margin-top: 4px;
  transform: rotate(225deg);
}

.option-select-menu {
  position: absolute;
  top: calc(100% + var(--space-2));
  left: 0;
  z-index: 40;
  width: 100%;
  max-height: 220px;
  overflow-y: auto;
  padding: var(--space-1);
  background: var(--color-surface-raised);
  border: 1px solid var(--color-border);
  border-radius: var(--radius-md);
  box-shadow: var(--shadow-lg);
}

.option-select-item {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: var(--space-3);
  width: 100%;
  min-height: 38px;
  padding: var(--space-2) var(--space-3);
  color: var(--color-text-muted);
  background: transparent;
  border: 0;
  border-radius: var(--radius-sm);
  font: inherit;
  font-size: var(--font-size-sm);
  font-weight: 500;
  text-align: left;
  cursor: pointer;
}

.option-select-item:hover,
.option-select-item:focus-visible,
.option-select-item.selected {
  color: var(--signal-active-strong);
  background: var(--color-brand-50);
  outline: none;
}

.option-select-check {
  width: 7px;
  height: 7px;
  flex: 0 0 7px;
  border-radius: var(--radius-pill);
  background: transparent;
}

.option-select-item.selected .option-select-check {
  background: var(--signal-active);
  box-shadow: 0 0 0 4px color-mix(in srgb, var(--signal-active) 16%, transparent);
}
</style>
